import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { message, Empty, Switch, Button, Modal } from 'antd'
import {
  ShopOutlined,
  SoundOutlined,
  UserOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  LoadingOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons'
import { SelectC } from '@/i2antdV5'
import Permission from '@/components/PermissionA5'
import { fetchYingshiDeviceList } from '@/redux/yingshi/deviceListSlice'
import { fetchStoreList } from '@/redux/dashboard/storeList/storeSlice'
import { fetchTrustAccessToken } from '@/redux/ys7/nvrSlice'
import { reconnectManager } from '@/utils/reconnectManager'
import {
  connectSse,
  disconnectSse,
  SSE_CONSUMER_IDS,
  addMonitoringAlarmProcessed,
  getMonitoringAlarmProcessedStreamKeysMap,
  getMonitoringAlarmSseList,
  getDepartureSseByStreamKey
} from '@/redux/sse/sseSlice'
import {
  applySurveillanceDeviceList,
  applySurveillanceReturnPending,
  markSurveillanceReturnAwaiting,
  removeSurveillanceDeparture,
  resetSurveillanceMessages,
  revertSurveillanceReturnAwaiting,
  syncSurveillanceDepartureSse,
  upsertSurveillanceDeparture
} from '@/redux/sse/surveillanceMessageSlice'
import {
  RETURN_AWAITING_GRACE_MINUTES,
  RETURN_AWAITING_GRACE_MS,
  buildListInitSceneEntry,
  enrichSurveillanceAlarmLevel,
  readMessageTimeMs
} from '@/redux/sse/surveillanceMessage'
import {
  getSeeData,
  getStreamKey,
  buildMonitoringStreamKeyFromDevice,
  getMonitoringSceneFlags,
  getMonitoringIsProcessed,
  isMonitoringAlertPayload
} from '@/views/commandCenter/profile/ServiceAlerts/monitoringSsePayloadUtils'
import {
  confirmDepartureRecordReturn,
  createDepartureRecord,
  deleteDepartureRecord,
  endDepartureNursing,
  markYingshiDeviceFalsePositive
} from '@/service/yingshi'
import { mapYingshiDevicesToMergedList } from '@/views/ys/mergeAccountAndTrustDevices'
import { SUR_BABYROOM_STORE } from '@/constants/menuPermissionMeta'
import SurveillanceCell, {
  CELL_STATE,
  DEPARTURE_TYPE,
  NURSING_NEXT_STEP,
  NURSING_TYPE_DICT_CODE,
  buildNursingLabelMap,
  resolveNursingNextStep
} from './components/SurveillanceCell'
import { fetchDictOptions } from '@/redux/components/dictOptionsSlice'
import { useSurveillanceTabletDensity } from './utils/surveillanceTabletDensity'
import SurveillanceAlertTtsSettingsModal from './alertTts/SurveillanceAlertTtsSettingsModal'
import StoreFaceLibraryDrawer from './face/StoreFaceLibraryDrawer'
import FaceCapture, { isFaceReadyForDestination } from '@/components/FaceCapture'
import { setFacePageStoreId } from './face/faceCaptureSession'
import {
  cancelSurveillanceAlertSpeak,
  enqueueSurveillanceAlertSpeak,
  markAlertTtsAudioUnlocked,
  pruneSpokenAlertStreamKeys,
  resetAlertTtsRuntimeState,
  resumeNoBabyAlertRepeats,
  syncNoBabyRepeatStreamKeys
} from './alertTts/surveillanceAlertTts'
import './List.less'

function buildDepartureStreamKey (deviceSerial, channelNo) {
  return buildMonitoringStreamKeyFromDevice(deviceSerial, channelNo)
}

/** 护理字典 value：消毒（时长在字典 extra，登记只传 subType） */
const DISINFECT_SUB_TYPE = 'disinfect'
const DISINFECT_FACE_KEY = 'surveillance-disinfect-all'

/** 本店还没有离室去向的摄像头 */
function listCamerasWithoutDeparture (devices, departureByStreamKey) {
  const rows = []
  const list = Array.isArray(devices) ? devices : []
  const departures = departureByStreamKey && typeof departureByStreamKey === 'object'
    ? departureByStreamKey
    : {}
  for (let i = 0; i < list.length; i += 1) {
    const device = list[i]
    const deviceId = device?.deviceSerial == null ? '' : String(device.deviceSerial).trim()
    const channel = device?.channelNo == null || device.channelNo === ''
      ? ''
      : String(device.channelNo).trim()
    if (!deviceId || channel === '') continue
    const streamKey = buildDepartureStreamKey(device.deviceSerial, device.channelNo)
    if (!streamKey || departures[streamKey]) continue
    rows.push(device)
  }
  return rows
}

/** 手动送回「待确认」：session 级，撑过页面刷新（换店清掉） */
const RETURN_AWAITING_STORAGE_PREFIX = 'sv_return_awaiting:'

/** 监控墙列表条件：SSE pageReload 整页刷新后恢复店铺/分屏/婴儿监控开关/整墙全屏 */
const SURVEILLANCE_LIST_CONDITIONS_KEY = 'surveillance.list.conditions'

function readSurveillanceListConditions () {
  try {
    const raw = window.sessionStorage.getItem(SURVEILLANCE_LIST_CONDITIONS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    return parsed
  } catch (e) {
    return null
  }
}

function writeSurveillanceListConditions (conditions) {
  try {
    const prev = readSurveillanceListConditions() || {}
    window.sessionStorage.setItem(
      SURVEILLANCE_LIST_CONDITIONS_KEY,
      JSON.stringify({ ...prev, ...(conditions || {}) })
    )
  } catch (e) {
    // ignore
  }
}

function readReturnAwaitingStorage (storeId) {
  if (storeId == null || String(storeId).trim() === '') return {}
  try {
    const raw = window.sessionStorage.getItem(RETURN_AWAITING_STORAGE_PREFIX + String(storeId))
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    return parsed
  } catch (err) {
    return {}
  }
}

function writeReturnAwaitingStorage (storeId, map) {
  if (storeId == null || String(storeId).trim() === '') return
  const key = RETURN_AWAITING_STORAGE_PREFIX + String(storeId)
  try {
    if (!map || typeof map !== 'object' || Object.keys(map).length === 0) {
      window.sessionStorage.removeItem(key)
      return
    }
    window.sessionStorage.setItem(key, JSON.stringify(map))
  } catch (err) {
    // 忽略配额 / 隐私模式
  }
}

/**
 * 合并推理告警态与离室登记态（列表 latestDepartureRecord + 本地登记/取消）
 */
function resolveEffectiveCellState (alarmCellState, departure) {
  if (!departure) return alarmCellState
  if (departure.status === 'return_pending') return CELL_STATE.RETURN_PENDING
  if (departure.status === 'return_awaiting') return CELL_STATE.RETURN_AWAITING
  if (departure.status === 'pre_departed') return CELL_STATE.PRE_DEPARTED
  if (departure.status === 'active') {
    return departure.type === DEPARTURE_TYPE.NURSING
      ? CELL_STATE.NURSING_IN_PROGRESS
      : CELL_STATE.DEPARTED
  }
  return alarmCellState
}

/** 分屏选项：id 即格数字符串 */
const SCREEN_LAYOUT_OPTIONS = [
  { id: '1', value: '1分屏' },
  { id: '4', value: '4分屏' },
  { id: '9', value: '9分屏' },
  { id: '12', value: '12分屏' },
  { id: '16', value: '16分屏' },
  { id: '25', value: '25分屏' },
  { id: '26', value: '26分屏' },
  { id: '28', value: '28分屏' },
  { id: '30', value: '30分屏' },
  { id: '32', value: '32分屏' },
  { id: '34', value: '34分屏' },
  { id: '35', value: '35分屏' },
  { id: '36', value: '36分屏' },
  { id: '37', value: '37分屏' },
  { id: '38', value: '38分屏' }
]

/** reconnectManager 注册 id，与页面卸载 unregister 成对 */
const SURVEILLANCE_DEVICE_LIST_RECONNECT_ID = 'surveillance-device-list'

function buildSurveillanceDeviceListQuery (formatQuery) {
  const storeId = formatQuery?.storeId
  if (storeId == null || String(storeId).trim() === '') return {}
  return { storeId: String(storeId).trim() }
}

const SURVEILLANCE_GRID_GUTTER = 8
const SURVEILLANCE_GRID_GUTTER_TABLET = 6
const SURVEILLANCE_MIN_CELL_WIDTH = 280
const SURVEILLANCE_MIN_CELL_WIDTH_TABLET = 180
/** 摄像头少时限制单格最大宽度，避免 1～2 路被 1fr 拉满整行 */
const SURVEILLANCE_MAX_CELL_WIDTH = 480
const SURVEILLANCE_MAX_CELL_WIDTH_TABLET = 320
const TABLET_LAYOUT_CHIPS = ['9', '16', '25']

function resolveScreenLayoutIdForDeviceCount (deviceCount) {
  const n = Number(deviceCount)
  if (!Number.isFinite(n) || n <= 0) return '1'
  const counts = SCREEN_LAYOUT_OPTIONS.map((o) => Number(o.id)).sort((a, b) => a - b)
  const hit = counts.find((c) => c >= n)
  return String(hit != null ? hit : counts[counts.length - 1])
}

function getLoginUserStoreId (user) {
  if (!user || typeof user !== 'object') return ''
  const raw = user.storeId != null && user.storeId !== '' ? user.storeId : user.store_id
  if (raw == null || raw === '') return ''
  return String(raw)
}

function resolveStoreLabel (storeOption, storeId) {
  if (!storeId) return '未关联店铺'
  const rows = Array.isArray(storeOption) ? storeOption : []
  const hit = rows.find((item) => String(item.id) === String(storeId))
  return hit ? hit.value : `店铺 ${storeId}`
}

/**
 * 预警「已持续」起点。
 * list 行用 alarmTimeMs（设备 alarmTime，与 latestMonitoringMessage 同层）。
 * SSE 用该条 messageTime，缺了不拿 alarmTime 或当前时间补。
 */
function readAlarmTimeMs (record) {
  const n = Number(record?.alarmTime)
  if (!Number.isFinite(n) || n <= 0) return 0
  return n < 1e12 ? n * 1000 : n
}

function resolveSurveillanceAlarmEventTimeMs (sceneEntry, device) {
  if (sceneEntry?.fromListInit !== true) {
    return readMessageTimeMs(sceneEntry?.seeData)
  }
  const fromDevice = Number(device?.alarmTimeMs) || readAlarmTimeMs(device)
  return fromDevice
}

/** 监控墙排序档：跟格子展示态一致，勿用裸 isAlarming（去游泳/在位会被误判成告警） */
const SURVEILLANCE_SORT_TIER = {
  ALERT: 0,
  DEPARTURE: 1,
  IN_PLACE: 2,
  EMPTY: 3
}

function getSurveillanceSortTier (cellState) {
  if (cellState === CELL_STATE.ALERT) return SURVEILLANCE_SORT_TIER.ALERT
  if (
    cellState === CELL_STATE.PRE_DEPARTED
    || cellState === CELL_STATE.DEPARTED
    || cellState === CELL_STATE.NURSING_IN_PROGRESS
    || cellState === CELL_STATE.RETURN_PENDING
    || cellState === CELL_STATE.RETURN_AWAITING
  ) {
    return SURVEILLANCE_SORT_TIER.DEPARTURE
  }
  if (cellState === CELL_STATE.EMPTY) return SURVEILLANCE_SORT_TIER.EMPTY
  return SURVEILLANCE_SORT_TIER.IN_PLACE
}

function compareDurationLongFirst (timeA, timeB) {
  if (timeA > 0 && timeB > 0 && timeA !== timeB) return timeA - timeB
  if (timeA > 0 && timeB <= 0) return -1
  if (timeB > 0 && timeA <= 0) return 1
  return 0
}

/** ALERT 档内是否「无婴儿」：场景看记录 hasBaby，否则回退 list.hasBaby */
function isSurveillanceSortNoBaby (device, sceneEntry) {
  if (sceneEntry?.seeData) {
    return getMonitoringSceneFlags(sceneEntry.seeData).hasBaby === false
  }
  return device?.hasBaby === false
}

/**
 * 监控墙排序：
 * 预警（非无婴儿 → 无婴儿；同组内等级→持续）→ 离室/护理（持续）→ 在位 → 未开推理；同档再按通道
 * （业务无「特急无婴儿」，无婴儿整体排在非无婴儿预警之后）
 */
function sortSurveillanceDevices (
  devices,
  sceneByStreamKey,
  departureByStreamKey = {}
) {
  return [...(devices || [])]
    .map((device, index) => {
      const streamKey = buildMonitoringStreamKeyFromDevice(device.deviceSerial, device.channelNo)
      const sceneEntry = sceneByStreamKey.get(streamKey) || null
      const departure = departureByStreamKey[streamKey] || null
      let alarmState = resolveCellState(device, sceneEntry)
      // 与格子展示同源：拼不出警告载荷的 ALERT 不当预警档
      if (
        alarmState === CELL_STATE.ALERT
        && !(sceneEntry || buildListInitSceneEntry(device))
      ) {
        alarmState = CELL_STATE.IN_PLACE
      }
      const cellState = resolveEffectiveCellState(alarmState, departure)
      const tier = getSurveillanceSortTier(cellState)
      let levelValue = 0
      let eventTimeMs = 0
      let isNoBaby = false
      if (tier === SURVEILLANCE_SORT_TIER.ALERT) {
        const levelFromScene = Number(sceneEntry?.levelValue) || 0
        const levelFromList = Number(device.alarmLevel) || 0
        levelValue = levelFromScene > 0 ? levelFromScene : levelFromList
        eventTimeMs = resolveSurveillanceAlarmEventTimeMs(sceneEntry, device)
        isNoBaby = isSurveillanceSortNoBaby(device, sceneEntry)
      } else if (tier === SURVEILLANCE_SORT_TIER.DEPARTURE) {
        eventTimeMs = Number(departure?.departedAt) || 0
      }
      return {
        device,
        index,
        tier,
        isNoBaby,
        levelValue,
        eventTimeMs,
        channelNo: Number(device.channelNo) || 0
      }
    })
    .sort((a, b) => {
      if (a.tier !== b.tier) return a.tier - b.tier
      if (a.tier === SURVEILLANCE_SORT_TIER.ALERT) {
        if (a.isNoBaby !== b.isNoBaby) return a.isNoBaby ? 1 : -1
        const levelDiff = (b.levelValue || 0) - (a.levelValue || 0)
        if (levelDiff !== 0) return levelDiff
        const durationDiff = compareDurationLongFirst(a.eventTimeMs, b.eventTimeMs)
        if (durationDiff !== 0) return durationDiff
      }
      if (a.tier === SURVEILLANCE_SORT_TIER.DEPARTURE) {
        const durationDiff = compareDurationLongFirst(a.eventTimeMs, b.eventTimeMs)
        if (durationDiff !== 0) return durationDiff
      }
      if (a.channelNo !== b.channelNo) return a.channelNo - b.channelNo
      return a.index - b.index
    })
    .map(({ device }) => device)
}

function resolveColsForContainerWidth (containerWidth, itemCount, { minCellWidth, gutter } = {}) {
  const count = Number(itemCount)
  const safeCount = Number.isFinite(count) && count > 0 ? count : 1
  if (!containerWidth || containerWidth <= 0) return 1
  const minW = minCellWidth > 0 ? minCellWidth : SURVEILLANCE_MIN_CELL_WIDTH
  const gap = gutter >= 0 ? gutter : SURVEILLANCE_GRID_GUTTER
  const maxColsByWidth = Math.max(1, Math.floor((containerWidth + gap) / (minW + gap)))
  return Math.min(safeCount, maxColsByWidth)
}

function getLayoutConfig (layoutId, containerWidth, itemCount, densityOpts) {
  const cols = resolveColsForContainerWidth(containerWidth, itemCount, densityOpts)
  const layoutCount = Number(layoutId)
  return { cols, rows: Math.ceil(layoutCount / cols) }
}

const SURVEILLANCE_CELL_LABEL_HEIGHT = 46
/** Pad 密看底栏：需容纳「去向」触控主按钮，与 List.less --tablet 对齐 */
const SURVEILLANCE_CELL_LABEL_HEIGHT_TABLET = 44

function getSurveillanceGridMetrics (containerWidth, cols, { gutter, labelHeight, maxCellWidth } = {}) {
  if (!containerWidth || !cols) return { cellWidth: 0, previewHeight: 0, cellHeight: 0 }
  const gap = gutter >= 0 ? gutter : SURVEILLANCE_GRID_GUTTER
  const labelH = labelHeight > 0 ? labelHeight : SURVEILLANCE_CELL_LABEL_HEIGHT
  const rawWidth = (containerWidth - (cols - 1) * gap) / cols
  const maxW = maxCellWidth > 0 ? maxCellWidth : Infinity
  const cellWidth = Math.min(rawWidth, maxW)
  const previewHeight = Math.floor((cellWidth * 9) / 16)
  const cellHeight = previewHeight + labelH
  return { cellWidth: Math.floor(cellWidth), previewHeight, cellHeight }
}

/**
 * 本地离室演示态优先时，不叠监控告警横幅（避免与去向/计时条抢视线）
 */
function isLocalDepartureCellState (cellState) {
  return cellState === CELL_STATE.PRE_DEPARTED
    || cellState === CELL_STATE.DEPARTED
    || cellState === CELL_STATE.NURSING_IN_PROGRESS
    || cellState === CELL_STATE.RETURN_PENDING
    || cellState === CELL_STATE.RETURN_AWAITING
}

/**
 * 格子告警态（离室在外层 resolveEffectiveCellState 让位）。
 * 是否告警只认记录上的 hasBaby===false 或 isViolation===true。
 * list 首屏把 isAlarming 写入 seeData.isViolation（见 buildListInitSceneEntry）。
 */
function resolveCellState (device, sceneEntry) {
  if (device.needInference !== true) return CELL_STATE.EMPTY
  if (isMonitoringAlertPayload(sceneEntry?.seeData)) return CELL_STATE.ALERT
  // 有婴儿、缺字段、无场景：都按非告警在位
  return CELL_STATE.IN_PLACE
}

function getPageFullscreenElement () {
  return document.fullscreenElement
    || document.webkitFullscreenElement
    || document.msFullscreenElement
    || null
}

/** 对指定节点请求全屏；旧 WebKit 可能无 Promise，统一包成 Promise */
function requestElementFullscreen (el) {
  if (!el) return Promise.reject(new Error('fullscreen target missing'))
  try {
    if (typeof el.requestFullscreen === 'function') {
      return Promise.resolve(el.requestFullscreen())
    }
    if (typeof el.webkitRequestFullscreen === 'function') {
      el.webkitRequestFullscreen()
      return Promise.resolve()
    }
    if (typeof el.webkitRequestFullScreen === 'function') {
      el.webkitRequestFullScreen()
      return Promise.resolve()
    }
    if (typeof el.msRequestFullscreen === 'function') {
      el.msRequestFullscreen()
      return Promise.resolve()
    }
  } catch (err) {
    return Promise.reject(err)
  }
  return Promise.reject(new Error('fullscreen unsupported'))
}

function exitPageFullscreen () {
  if (!getPageFullscreenElement()) return Promise.resolve()
  try {
    if (typeof document.exitFullscreen === 'function') {
      return Promise.resolve(document.exitFullscreen())
    }
    if (typeof document.webkitExitFullscreen === 'function') {
      document.webkitExitFullscreen()
      return Promise.resolve()
    }
    if (typeof document.webkitCancelFullScreen === 'function') {
      document.webkitCancelFullScreen()
      return Promise.resolve()
    }
    if (typeof document.msExitFullscreen === 'function') {
      document.msExitFullscreen()
      return Promise.resolve()
    }
  } catch (err) {
    return Promise.reject(err)
  }
  return Promise.resolve()
}

export default function SurveillanceList () {
  const dispatch = useDispatch()
  const yingshiDeviceList = useSelector((state) => state.yingshi.deviceList)
  const storeList = useSelector((state) => state.dashboard.storeList)
  const app = useSelector((state) => state.app)
  const dictOptions = useSelector((state) => state.components.dictOptions)
  // 入库已按摄像头覆盖；带 messageBO 的兼容 message 也在 slice 里并入同桶
  const monitoringAlarmSseList = useSelector((s) => getMonitoringAlarmSseList(s.sse))
  const monitoringAlarmProcessedKeys = useSelector((s) => getMonitoringAlarmProcessedStreamKeysMap(s.sse))
  const departureSseByStreamKey = useSelector((s) => getDepartureSseByStreamKey(s.sse))
  const surveillanceMessage = useSelector((state) => state.surveillanceMessage)
  const {
    departureByStreamKey,
    sceneByStreamKey: sceneByStreamKeyObj
  } = surveillanceMessage.data

  const { nursing_type: nursingTypeDict } = dictOptions
  const nursingLabelMap = useMemo(
    () => buildNursingLabelMap(nursingTypeDict.data),
    [nursingTypeDict.data]
  )

  const { user, permissionCodes, permissionCodesLoaded } = app
  const { default_storeOption: storeOption = [] } = storeList
  const buttonCodes = permissionCodes?.buttonCodes
  const canSelectStore = permissionCodesLoaded
    && Array.isArray(buttonCodes)
    && buttonCodes.includes(SUR_BABYROOM_STORE)
  const { data: yingshiDevices, fetching: deviceFetching, query: deviceListQuery } = yingshiDeviceList

  const isTabletDensity = useSurveillanceTabletDensity()
  const savedListConditions = readSurveillanceListConditions()
  const [toolbarQuery, setToolbarQuery] = useState(() => ({
    screenLayoutId: savedListConditions?.screenLayoutId != null
      && String(savedListConditions.screenLayoutId).trim() !== ''
      ? String(savedListConditions.screenLayoutId)
      : '1',
    storeId: savedListConditions?.storeId != null
      && String(savedListConditions.storeId).trim() !== ''
      ? String(savedListConditions.storeId)
      : ''
  }))
  const screenLayoutId = toolbarQuery.screenLayoutId
  const [inferenceOnly, setInferenceOnly] = useState(() => (
    typeof savedListConditions?.inferenceOnly === 'boolean'
      ? savedListConditions.inferenceOnly
      : true
  ))
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  /** 驱动「已持续」时长刷新；仅需要时以较低频率 tick */
  const [nowTick, setNowTick] = useState(() => Date.now())
  /** 错检已点、列表还没回来：这路按钮禁选 */
  const [wrongCheckPending, setWrongCheckPending] = useState({})
  const wrongCheckPendingRef = useRef({})
  /** 已提交、接口还没回：挡无婴儿播报 */
  const noBabySpeakBlockedKeysRef = useRef(new Set())
  const [faceLibraryOpen, setFaceLibraryOpen] = useState(false)
  const [disinfectOpen, setDisinfectOpen] = useState(false)
  const [disinfectFace, setDisinfectFace] = useState(null)
  const disinfectFaceRef = useRef(null)
  const [disinfectSubmitting, setDisinfectSubmitting] = useState(false)
  const [disinfectWaitText, setDisinfectWaitText] = useState('')
  const disinfectSubmitRef = useRef(false)
  /** 整墙全屏：点击时走 Fullscreen API；刷新后只能恢复藏壳伪全屏（浏览器禁止无手势再进原生全屏） */
  const [wallFullscreen, setWallFullscreen] = useState(() => savedListConditions?.wallFullscreen === true)
  /** 全屏时「退出」按钮：鼠标静止 1 分钟后隐藏，移动再显示 */
  const [exitChromeVisible, setExitChromeVisible] = useState(true)
  const pageRef = useRef(null)
  const containerRef = useRef(null)
  const exitChromeIdleTimerRef = useRef(null)
  // pageReload 恢复过分屏：视为手动布局，避免进页被设备数自动改掉
  const layoutManuallySetRef = useRef(
    Boolean(
      savedListConditions?.screenLayoutId != null
      && String(savedListConditions.screenLayoutId).trim() !== ''
    )
  )
  const pendingStoreFetchRef = useRef('')
  const lastDeviceListFetchAtRef = useRef(0)
  /** SSE 重连后重拉列表用：避免切店反复 register */
  const effectiveStoreIdRef = useRef('')
  /** 待确认空档定时：streamKey → { timerId, returnAt } */
  const returnAwaitingTimersRef = useRef({})
  /** 离室 SSE 已应用到格子的 seq，避免同条反复改状态 */
  const appliedDepartureSseSeqRef = useRef({})
  /** SSE 播报差分：首帧只建基线不播，避免进页连环念 */
  const sseSpeakPrimedRef = useRef(false)
  const prevSseAlarmStreamKeysRef = useRef(new Set())

  const densityOpts = useMemo(() => (
    isTabletDensity
      ? {
        minCellWidth: SURVEILLANCE_MIN_CELL_WIDTH_TABLET,
        maxCellWidth: SURVEILLANCE_MAX_CELL_WIDTH_TABLET,
        gutter: SURVEILLANCE_GRID_GUTTER_TABLET,
        labelHeight: SURVEILLANCE_CELL_LABEL_HEIGHT_TABLET
      }
      : {
        minCellWidth: SURVEILLANCE_MIN_CELL_WIDTH,
        maxCellWidth: SURVEILLANCE_MAX_CELL_WIDTH,
        gutter: SURVEILLANCE_GRID_GUTTER,
        labelHeight: SURVEILLANCE_CELL_LABEL_HEIGHT
      }
  ), [isTabletDensity])

  const updateContainerSize = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const width = Math.floor(rect.width)
    const height = Math.floor(rect.height)
    if (width <= 0 && height <= 0) return
    setContainerSize((prev) => {
      if (prev.width === width && prev.height === height) return prev
      return { width, height }
    })
  }, [])

  const userStoreId = getLoginUserStoreId(user)
  const selectedStoreId = toolbarQuery.storeId
  const effectiveStoreId = permissionCodesLoaded
    ? (canSelectStore ? selectedStoreId : userStoreId)
    : ''
  const storeLabel = resolveStoreLabel(storeOption, userStoreId)

  const devicesMatchStore = effectiveStoreId != null && String(effectiveStoreId).trim() !== '' && String(deviceListQuery?.storeId ?? '') === String(effectiveStoreId)
  // 同店后台重拉不清空 list；有本店数据时不要当首屏 loading，避免中间闪「加载中」/格子转圈
  const hasStoreList = devicesMatchStore && Array.isArray(yingshiDevices) && yingshiDevices.length > 0
  const monitoringBaselineReady = devicesMatchStore && (!deviceFetching || hasStoreList)
  const listPending = !permissionCodesLoaded
    || (Boolean(effectiveStoreId) && (!devicesMatchStore || (deviceFetching && !hasStoreList)))

  const mergedDevices = useMemo(() =>
    devicesMatchStore ? mapYingshiDevicesToMergedList(yingshiDevices) : [],
    [yingshiDevices, devicesMatchStore]
  )
  const mergedDevicesRef = useRef(mergedDevices)
  mergedDevicesRef.current = mergedDevices
  const disinfectTargets = useMemo(
    () => listCamerasWithoutDeparture(mergedDevices, departureByStreamKey),
    [mergedDevices, departureByStreamKey]
  )
  const disinfectLabel = nursingLabelMap[DISINFECT_SUB_TYPE]?.label || '消毒'
  const monitoringAlarmSseListRef = useRef(monitoringAlarmSseList)
  monitoringAlarmSseListRef.current = monitoringAlarmSseList
  const monitoringAlarmProcessedKeysRef = useRef(monitoringAlarmProcessedKeys)
  monitoringAlarmProcessedKeysRef.current = monitoringAlarmProcessedKeys

  const sceneByStreamKey = useMemo(
    () => new Map(Object.entries(sceneByStreamKeyObj)),
    [sceneByStreamKeyObj]
  )

  const storeSelectOptions = useMemo(() => {
    if (canSelectStore) return Array.isArray(storeOption) ? storeOption : []
    if (!userStoreId) return []
    return [{ id: userStoreId, value: storeLabel }]
  }, [canSelectStore, storeOption, userStoreId, storeLabel])

  const handleScreenLayoutChange = useCallback((objValue) => {
    setToolbarQuery((prev) => {
      if (objValue.screenLayoutId != null && String(objValue.screenLayoutId) !== String(prev.screenLayoutId)) {
        layoutManuallySetRef.current = true
      }
      return { ...prev, ...objValue }
    })
  }, [])

  const fetchDeviceList = useCallback((formatQuery) => {
    const storeId = formatQuery?.storeId != null && String(formatQuery.storeId).trim() !== '' ? String(formatQuery.storeId).trim() : ''
    if (!storeId) return
    pendingStoreFetchRef.current = storeId
    lastDeviceListFetchAtRef.current = Date.now()
    setToolbarQuery((prev) => ({ screenLayoutId: formatQuery.screenLayoutId ?? prev.screenLayoutId, storeId }))
    return dispatch(fetchYingshiDeviceList({ query: buildSurveillanceDeviceListQuery(formatQuery) }))
      .unwrap()
      .catch(() => {})
  }, [dispatch])

  /** 换店清掉上一店的离室和场景；加载中的空档不清，避免刚到的 SSE 被抹掉 */
  useEffect(() => {
    noBabySpeakBlockedKeysRef.current = new Set()
    wrongCheckPendingRef.current = {}
    setWrongCheckPending({})
    dispatch(resetSurveillanceMessages())
  }, [effectiveStoreId, dispatch])

  /** 列表刷新 / 首屏：设备行铺底，已有 SSE 场景盖住对应路 */
  useEffect(() => {
    if (!devicesMatchStore) return
    dispatch(applySurveillanceDeviceList({
      devices: mergedDevices,
      awaitingSaved: readReturnAwaitingStorage(effectiveStoreId),
      monitoringAlarmSseList: monitoringAlarmSseListRef.current,
      monitoringAlarmProcessedKeys: monitoringAlarmProcessedKeysRef.current
    }))
  }, [mergedDevices, devicesMatchStore, effectiveStoreId, dispatch])

  /** 离室 SSE 已在 addSseMessage 里写入；这里补上换店重置后还留在桶里的最新一条 */
  useEffect(() => {
    if (!devicesMatchStore) return
    dispatch(syncSurveillanceDepartureSse({ departureSseByStreamKey }))
  }, [devicesMatchStore, departureSseByStreamKey, dispatch])

  /** 待确认落 session，刷新后可恢复再做 hasBaby 校验 */
  useEffect(() => {
    if (!effectiveStoreId) return
    const map = {}
    Object.keys(departureByStreamKey).forEach((streamKey) => {
      const dep = departureByStreamKey[streamKey]
      if (!dep || dep.status !== 'return_awaiting') return
      map[streamKey] = {
        id: dep.id,
        priorStatus: dep.priorStatus,
        returnAt: dep.returnAt,
        type: dep.type,
        subType: dep.subType,
        nurseId: dep.nurseId,
        nurseName: dep.nurseName,
        departedAt: dep.departedAt
      }
    })
    writeReturnAwaitingStorage(effectiveStoreId, map)
  }, [departureByStreamKey, effectiveStoreId])

  const displayDevices = useMemo(() => {
    if (!inferenceOnly) return mergedDevices
    return mergedDevices.filter((d) => d.needInference === true)
  }, [mergedDevices, inferenceOnly])

  const sortedDevices = useMemo(() =>
    sortSurveillanceDevices(
      displayDevices,
      sceneByStreamKey,
      departureByStreamKey
    ),
    [displayDevices, sceneByStreamKey, departureByStreamKey]
  )

  const handleInferenceOnlyChange = useCallback((checked) => {
    layoutManuallySetRef.current = false
    setInferenceOnly(checked)
  }, [])

  /** 仅「展示持续时长」的格子需要 tick（预警已持续 / 离室计时） */
  const needsDurationTick = useMemo(() => {
    const hasTimedDeparture = Object.values(departureByStreamKey).some(
      (item) => item
        && item.departedAt
        && (item.status === 'active' || item.status === 'return_awaiting')
    )
    if (hasTimedDeparture) return true
    return sortedDevices.some((device) => {
      if (device.needInference !== true) return false
      const sk = buildMonitoringStreamKeyFromDevice(device.deviceSerial, device.channelNo)
      if (departureByStreamKey[sk]) return false
      const alarm = sceneByStreamKey.get(sk) || null
      if (!alarm) return false
      // 与格子展示同源：ALERT 场景都要刷「已持续」，勿因 ticketClosed 停表
      if (resolveCellState(device, alarm) !== CELL_STATE.ALERT) return false
      return Boolean(alarm.eventTimeMs || device.alarmTimeMs)
    })
  }, [sortedDevices, sceneByStreamKey, departureByStreamKey])

  useEffect(() => {
    if (!needsDurationTick) return undefined
    // 与原先一致：每秒刷新「已持续」，避免 5s 一跳观感不对
    const timerId = window.setInterval(() => setNowTick(Date.now()), 1000)
    return () => window.clearInterval(timerId)
  }, [needsDurationTick])

  useEffect(() => () => {
    Object.values(returnAwaitingTimersRef.current).forEach((entry) => {
      if (entry && entry.timerId) window.clearTimeout(entry.timerId)
    })
    returnAwaitingTimersRef.current = {}
  }, [])

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        cancelSurveillanceAlertSpeak()
        return
      }
      resumeNoBabyAlertRepeats()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      resetAlertTtsRuntimeState()
    }
  }, [])

  /**
   * 仅对 SSE monitoring_alarm 增量播报；HTTP 未处理基线不入队。
   * 入库已按摄像头每路 1 条：活跃集直接扫列表，不再同路多条里挑。
   * 首帧建立 prev 基线不播；同路已活跃不重复；离开活跃集后清除已播，便于再次告警再播。
   * 无婴儿的再次念在播报队列里按间隔入队，这里只把仍是无婴儿的路交过去。
   */
  useEffect(() => {
    if (!monitoringBaselineReady) return
    const alertItemByStreamKey = new Map()
    const noBabyKeys = new Set()
    for (let i = 0; i < monitoringAlarmSseList.length; i += 1) {
      const item = monitoringAlarmSseList[i]
      if (!item || item._javaMonitoringAlarm) continue
      const sk = getStreamKey(item)
      if (!sk || monitoringAlarmProcessedKeys[sk]) continue
      // 工单已处理：只停播报，不在此改格子态
      if (getMonitoringIsProcessed(item) === true) continue
      const seeFlags = getMonitoringSceneFlags(getSeeData(item))
      if (seeFlags.hasBaby !== false && seeFlags.isViolation !== true) continue
      // 已登记去向后，空床是离室结果，后续无婴儿推送不再播报
      const departure = departureByStreamKey[sk]
      const departureOpen = departure
        && (
          departure.status === 'pre_departed'
          || departure.status === 'active'
          || departure.status === 'return_pending'
          || departure.status === 'return_awaiting'
        )
      if (
        seeFlags.hasBaby === false
        && (departureOpen || noBabySpeakBlockedKeysRef.current.has(sk))
      ) continue
      alertItemByStreamKey.set(sk, item)
      if (seeFlags.hasBaby === false) noBabyKeys.add(sk)
    }

    const activeKeys = new Set(alertItemByStreamKey.keys())
    pruneSpokenAlertStreamKeys(activeKeys)
    syncNoBabyRepeatStreamKeys(noBabyKeys)

    if (!sseSpeakPrimedRef.current) {
      prevSseAlarmStreamKeysRef.current = activeKeys
      sseSpeakPrimedRef.current = true
      return
    }

    activeKeys.forEach((sk) => {
      if (prevSseAlarmStreamKeysRef.current.has(sk)) return
      const item = alertItemByStreamKey.get(sk)
      if (!item) return
      const seeData = getSeeData(item)
      const deviceRow = mergedDevices.find((d) => (
        buildMonitoringStreamKeyFromDevice(d.deviceSerial, d.channelNo) === sk
      )) || null
      if (!deviceRow) return
      enqueueSurveillanceAlertSpeak({
        streamKey: sk,
        seeData,
        deviceRow
      })
    })

    prevSseAlarmStreamKeysRef.current = activeKeys
  }, [
    monitoringAlarmSseList,
    monitoringAlarmProcessedKeys,
    monitoringBaselineReady,
    mergedDevices,
    departureByStreamKey
  ])

  useEffect(() => {
    writeSurveillanceListConditions({
      storeId: toolbarQuery.storeId,
      screenLayoutId: toolbarQuery.screenLayoutId,
      inferenceOnly
    })
  }, [toolbarQuery, inferenceOnly])

  useEffect(() => {
    dispatch(fetchStoreList({ serviceType: 'default', dataType: 'storeOption' }))
    dispatch(fetchDictOptions({ typeCode: NURSING_TYPE_DICT_CODE }))
  }, [dispatch])

  const enterWallFullscreen = useCallback(() => {
    // 刷新会先退出原生全屏再 reload；整墙意图必须同步写入，不能等 fullscreenchange。
    writeSurveillanceListConditions({ wallFullscreen: true })
    setWallFullscreen(true)
    // 必须全屏 html：去向盘 Modal / Tooltip / message 都挂在 body。
    // 若全屏 pageRef，body 上的弹层在全屏树外，点「去向」无反应。
    const target = document.documentElement
    requestElementFullscreen(target).catch(() => {
      setWallFullscreen(true)
    })
  }, [])

  const leaveWallFullscreen = useCallback(() => {
    writeSurveillanceListConditions({ wallFullscreen: false })
    setWallFullscreen(false)
    if (getPageFullscreenElement()) {
      exitPageFullscreen().catch(() => {})
    }
  }, [])

  // 原生全屏被浏览器拆掉时只改 UI；刷新卸载也会 fire，不能在这里写成 false
  useEffect(() => {
    const syncFromBrowser = () => {
      if (getPageFullscreenElement()) setWallFullscreen(true)
    }
    document.addEventListener('fullscreenchange', syncFromBrowser)
    document.addEventListener('webkitfullscreenchange', syncFromBrowser)
    return () => {
      document.removeEventListener('fullscreenchange', syncFromBrowser)
      document.removeEventListener('webkitfullscreenchange', syncFromBrowser)
    }
  }, [])

  // 只负责藏壳 class + 重算格子；禁止在 cleanup 里 exitFullscreen（进全屏时 false→true 会误退）
  useEffect(() => {
    const root = document.documentElement
    if (wallFullscreen) root.classList.add('surveillance-wall-fullscreen')
    else root.classList.remove('surveillance-wall-fullscreen')
    let raf2 = 0
    const raf1 = window.requestAnimationFrame(() => {
      updateContainerSize()
      raf2 = window.requestAnimationFrame(updateContainerSize)
    })
    return () => {
      window.cancelAnimationFrame(raf1)
      if (raf2) window.cancelAnimationFrame(raf2)
    }
  }, [wallFullscreen, updateContainerSize])

  // 卸载页时清 class / 退出浏览器全屏
  useEffect(() => () => {
    document.documentElement.classList.remove('surveillance-wall-fullscreen')
    if (getPageFullscreenElement()) {
      exitPageFullscreen().catch(() => {})
    }
  }, [])

  // Esc 退出整墙（含伪全屏）；原生全屏由浏览器同时退出。刷新不会按 Esc，故不会误清存储
  useEffect(() => {
    if (!wallFullscreen) return undefined
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return
      writeSurveillanceListConditions({ wallFullscreen: false })
      setWallFullscreen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [wallFullscreen])

  // 全屏退出按钮：静止 1 分钟隐藏，指针再动显示
  useEffect(() => {
    if (!wallFullscreen) {
      setExitChromeVisible(true)
      if (exitChromeIdleTimerRef.current) {
        clearTimeout(exitChromeIdleTimerRef.current)
        exitChromeIdleTimerRef.current = null
      }
      return undefined
    }
    const IDLE_MS = 60 * 1000
    const bump = () => {
      setExitChromeVisible(true)
      if (exitChromeIdleTimerRef.current) clearTimeout(exitChromeIdleTimerRef.current)
      exitChromeIdleTimerRef.current = setTimeout(() => {
        setExitChromeVisible(false)
        exitChromeIdleTimerRef.current = null
      }, IDLE_MS)
    }
    bump()
    const opts = { passive: true }
    window.addEventListener('pointermove', bump, opts)
    window.addEventListener('pointerdown', bump, opts)
    return () => {
      window.removeEventListener('pointermove', bump, opts)
      window.removeEventListener('pointerdown', bump, opts)
      if (exitChromeIdleTimerRef.current) {
        clearTimeout(exitChromeIdleTimerRef.current)
        exitChromeIdleTimerRef.current = null
      }
    }
  }, [wallFullscreen])

  useEffect(() => {
    if (!permissionCodesLoaded) return
    if (canSelectStore) {
      setToolbarQuery((prev) => {
        if (prev.storeId) return prev
        const fallbackId = userStoreId || (storeOption[0]?.id != null ? String(storeOption[0].id) : '')
        if (!fallbackId) return prev
        return { ...prev, storeId: fallbackId }
      })
      return
    }
    if (!userStoreId) return
    setToolbarQuery((prev) => prev.storeId === userStoreId ? prev : { ...prev, storeId: userStoreId })
  }, [permissionCodesLoaded, canSelectStore, userStoreId, storeOption])

  useEffect(() => { layoutManuallySetRef.current = false }, [effectiveStoreId])

  useEffect(() => {
    // 切店：播报差分重基线，避免旧店活跃键挡住新店首告
    sseSpeakPrimedRef.current = false
    prevSseAlarmStreamKeysRef.current = new Set()
  }, [effectiveStoreId])

  // 刷脸 recognize 直接读当前页门店（与人脸库 list/register/delete 同源）
  useEffect(() => {
    setFacePageStoreId(effectiveStoreId)
    return () => {
      setFacePageStoreId('')
    }
  }, [effectiveStoreId])

  useEffect(() => {
    if (listPending || !effectiveStoreId || !devicesMatchStore) return
    if (layoutManuallySetRef.current) return
    // 按设备数适配分屏，避免默认截断导致「摄像头不见了」
    const nextLayoutId = resolveScreenLayoutIdForDeviceCount(displayDevices.length)
    setToolbarQuery((prev) => String(prev.screenLayoutId) === nextLayoutId ? prev : { ...prev, screenLayoutId: nextLayoutId })
  }, [listPending, effectiveStoreId, devicesMatchStore, displayDevices.length, inferenceOnly])

  useEffect(() => {
    if (!effectiveStoreId) return
    if (pendingStoreFetchRef.current === String(effectiveStoreId)) { pendingStoreFetchRef.current = ''; return }
    dispatch(fetchTrustAccessToken())
    fetchDeviceList({ storeId: effectiveStoreId })
  }, [dispatch, effectiveStoreId, fetchDeviceList])

  effectiveStoreIdRef.current = effectiveStoreId || ''

  useEffect(() => {
    dispatch(connectSse({ consumerId: SSE_CONSUMER_IDS.SURVEILLANCE_LIST }))
    return () => dispatch(disconnectSse({ consumerId: SSE_CONSUMER_IDS.SURVEILLANCE_LIST }))
  }, [dispatch])

  /** 断连期间推送收不到（如解除包）；SSE 重连成功后重拉列表对回真值。只挂一次，storeId 走 ref。 */
  useEffect(() => {
    reconnectManager.register({
      id: SURVEILLANCE_DEVICE_LIST_RECONNECT_ID,
      type: 'api',
      reconnectOnOnline: false,
      heartbeatEnabled: false,
      refetchAfterJavaSseReconnect: true,
      reconnect: () => {
        const storeId = effectiveStoreIdRef.current
        if (!storeId) return
        if (Date.now() - lastDeviceListFetchAtRef.current < 8000) return
        lastDeviceListFetchAtRef.current = Date.now()
        dispatch(fetchYingshiDeviceList({
          query: buildSurveillanceDeviceListQuery({ storeId })
        }))
      }
    })
    return () => reconnectManager.unregister(SURVEILLANCE_DEVICE_LIST_RECONNECT_ID)
  }, [dispatch])

  const handleLayoutChipClick = useCallback((layoutId) => () => {
    layoutManuallySetRef.current = true
    setToolbarQuery((prev) => (
      String(prev.screenLayoutId) === String(layoutId)
        ? prev
        : { ...prev, screenLayoutId: String(layoutId) }
    ))
  }, [])

  const devicesToShow = useMemo(() => sortedDevices.slice(0, Number(screenLayoutId)), [sortedDevices, screenLayoutId])

  const { cols } = useMemo(
    () => getLayoutConfig(screenLayoutId, containerSize.width, devicesToShow.length, densityOpts),
    [screenLayoutId, containerSize.width, devicesToShow.length, densityOpts]
  )

  const gridMetrics = useMemo(
    () => getSurveillanceGridMetrics(containerSize.width, cols, densityOpts),
    [containerSize.width, cols, densityOpts]
  )

  const departureByStreamKeyRef = useRef(departureByStreamKey)
  departureByStreamKeyRef.current = departureByStreamKey
  const sceneByStreamKeyRef = useRef(sceneByStreamKey)
  sceneByStreamKeyRef.current = sceneByStreamKey

  const clearReturnAwaitingTimer = useCallback((streamKey) => {
    const entry = returnAwaitingTimersRef.current[streamKey]
    if (entry && entry.timerId) {
      window.clearTimeout(entry.timerId)
    }
    delete returnAwaitingTimersRef.current[streamKey]
  }, [])

  /**
   * 待确认空档：超时仍未见宝宝 → 退回 priorStatus，继续从 departedAt 计时
   */
  const revertReturnAwaitingToPrior = useCallback((streamKey) => {
    clearReturnAwaitingTimer(streamKey)
    const cur = departureByStreamKeyRef.current[streamKey]
    if (!cur || cur.status !== 'return_awaiting') return
    dispatch(revertSurveillanceReturnAwaiting({ streamKey }))
    message.info('空档已过仍未见宝宝，已恢复为离室登记，继续计时')
  }, [clearReturnAwaitingTimer, dispatch])

  const scheduleReturnAwaitingGrace = useCallback((streamKey, returnAt) => {
    const existing = returnAwaitingTimersRef.current[streamKey]
    if (existing && existing.returnAt === returnAt) return
    clearReturnAwaitingTimer(streamKey)
    const started = Number(returnAt)
    const deadline = (Number.isFinite(started) ? started : Date.now()) + RETURN_AWAITING_GRACE_MS
    const delay = Math.max(0, deadline - Date.now())
    const timerId = window.setTimeout(() => {
      delete returnAwaitingTimersRef.current[streamKey]
      revertReturnAwaitingToPrior(streamKey)
    }, delay)
    returnAwaitingTimersRef.current[streamKey] = { timerId, returnAt }
  }, [clearReturnAwaitingTimer, revertReturnAwaitingToPrior])

  /** 有待确认则挂空档定时；离开待确认则清掉 */
  useEffect(() => {
    const activeKeys = new Set()
    Object.keys(departureByStreamKey).forEach((streamKey) => {
      const dep = departureByStreamKey[streamKey]
      if (!dep || dep.status !== 'return_awaiting') return
      activeKeys.add(streamKey)
      scheduleReturnAwaitingGrace(streamKey, dep.returnAt)
    })
    Object.keys(returnAwaitingTimersRef.current).forEach((streamKey) => {
      if (activeKeys.has(streamKey)) return
      clearReturnAwaitingTimer(streamKey)
    })
  }, [departureByStreamKey, scheduleReturnAwaitingGrace, clearReturnAwaitingTimer])

  /**
   * 离室 SSE 的格子数据在 surveillanceMessage。这里只做提示、播报和空档定时。
   */
  useEffect(() => {
    if (!devicesMatchStore) return
    Object.keys(departureSseByStreamKey).forEach((streamKey) => {
      const evt = departureSseByStreamKey[streamKey]
      if (!evt || appliedDepartureSseSeqRef.current[streamKey] === evt.seq) return
      appliedDepartureSseSeqRef.current[streamKey] = evt.seq
      if (evt.eventType === 'departure_timeout_warning') {
        message.warning('离室业务已经超时，请更新状态')
        enqueueSurveillanceAlertSpeak({
          streamKey: `${streamKey}:departure_timeout:${evt.seq}`,
          text: '离室业务已经超时，请更新状态'
        })
        return
      }

      const payload = evt.payload || {}
      const bed = String(payload.status == null ? '' : payload.status).trim().toLowerCase().replace(/-/g, '_')

      if (bed === 'in_place' || bed === 'completed') {
        noBabySpeakBlockedKeysRef.current.delete(streamKey)
        clearReturnAwaitingTimer(streamKey)
        message.success('宝宝已回在位')
        return
      }

      if (
        bed === 'active'
        || bed === 'departed'
        || bed === 'pre_departed'
        || bed === 'return_pending'
      ) {
        if (bed === 'return_pending') clearReturnAwaitingTimer(streamKey)
        noBabySpeakBlockedKeysRef.current.add(streamKey)
      }
    })
  }, [devicesMatchStore, departureSseByStreamKey, clearReturnAwaitingTimer])

  const closeDisinfect = useCallback(() => {
    if (disinfectSubmitting) return
    if (disinfectFaceRef.current) disinfectFaceRef.current.stop()
    setDisinfectOpen(false)
  }, [disinfectSubmitting])

  const openDisinfect = useCallback(() => {
    const dictList = Array.isArray(nursingTypeDict.data) ? nursingTypeDict.data : []
    const hasDisinfect = dictList.some((item) => (
      item?.value != null && String(item.value) === DISINFECT_SUB_TYPE
    ))
    if (!hasDisinfect) {
      message.warning('字典里还没有消毒')
      return
    }
    const targets = listCamerasWithoutDeparture(
      mergedDevicesRef.current,
      departureByStreamKeyRef.current
    )
    if (targets.length === 0) {
      message.info('没有未登记去向的摄像头')
      return
    }
    if (disinfectFaceRef.current) disinfectFaceRef.current.start()
    setDisinfectOpen(true)
  }, [nursingTypeDict.data])

  const confirmDisinfect = useCallback(async () => {
    if (disinfectSubmitRef.current || disinfectSubmitting) return
    disinfectSubmitRef.current = true
    let faceResult
    try {
      faceResult = disinfectFaceRef.current
        ? await disinfectFaceRef.current.awaitResult()
        : { ok: false }
    } catch (err) {
      disinfectSubmitRef.current = false
      message.warning('请先刷脸确认身份')
      return
    }
    const hasCapture = faceResult?.ok
      && faceResult.nurseId
      && faceResult.nurseName
      && typeof Blob !== 'undefined'
      && faceResult.nurseAvatar instanceof Blob
    if (!hasCapture) {
      disinfectSubmitRef.current = false
      message.warning('请先刷脸确认身份')
      return
    }

    const targets = listCamerasWithoutDeparture(
      mergedDevicesRef.current,
      departureByStreamKeyRef.current
    )
    if (targets.length === 0) {
      disinfectSubmitRef.current = false
      message.info('没有未登记去向的摄像头')
      if (disinfectFaceRef.current) disinfectFaceRef.current.stop()
      setDisinfectOpen(false)
      return
    }

    setDisinfectSubmitting(true)
    setDisinfectOpen(false)
    setDisinfectWaitText(`正在登记消毒 0/${targets.length}`)
    if (disinfectFaceRef.current) disinfectFaceRef.current.stop()
    let ok = 0
    let fail = 0
    let firstErr = ''
    try {
      for (let i = 0; i < targets.length; i += 1) {
        setDisinfectWaitText(`正在登记消毒 ${i + 1}/${targets.length}`)
        const device = targets[i]
        const deviceId = String(device.deviceSerial).trim()
        const channel = String(device.channelNo).trim()
        const streamKey = buildDepartureStreamKey(device.deviceSerial, device.channelNo)
        if (!streamKey) continue
        const sceneEntry = sceneByStreamKeyRef.current.get(streamKey) || null
        const fromAlarm = resolveCellState(device, sceneEntry) === CELL_STATE.ALERT
        noBabySpeakBlockedKeysRef.current.add(streamKey)
        try {
          const response = await createDepartureRecord({
            deviceId,
            channelNo: channel,
            nurseId: faceResult.nurseId,
            type: DEPARTURE_TYPE.NURSING,
            subType: DISINFECT_SUB_TYPE,
            nurseAvatar: faceResult.nurseAvatar
          })
          const recordId = response?.data?.id != null && response.data.id !== ''
            ? response.data.id
            : `dep_${Date.now()}_${channel}`
          const listAlarmMs = Number(device.alarmTimeMs) || 0
          const row = {
            id: recordId,
            type: DEPARTURE_TYPE.NURSING,
            subType: DISINFECT_SUB_TYPE,
            nurseId: faceResult.nurseId,
            nurseName: faceResult.nurseName,
            status: fromAlarm ? 'active' : 'pre_departed'
          }
          if (listAlarmMs > 0) row.departedAt = listAlarmMs
          departureByStreamKeyRef.current = {
            ...departureByStreamKeyRef.current,
            [streamKey]: row
          }
          dispatch(upsertSurveillanceDeparture({ streamKey, row }))
          if (fromAlarm) {
            dispatch(addMonitoringAlarmProcessed({
              deviceId,
              channelNo: channel
            }))
          }
          ok += 1
        } catch (error) {
          noBabySpeakBlockedKeysRef.current.delete(streamKey)
          fail += 1
          if (!firstErr) {
            firstErr = (error && typeof error === 'object' && (error.msg || error.message))
              || '登记去向失败'
          }
        }
      }
      setDisinfectWaitText('正在刷新监控列表…')
      if (effectiveStoreId) {
        await fetchDeviceList({ storeId: effectiveStoreId })
      }
    } finally {
      disinfectSubmitRef.current = false
      setDisinfectSubmitting(false)
      setDisinfectWaitText('')
      setDisinfectOpen(false)
    }

    if (ok > 0 && fail === 0) {
      message.success(`已登记${disinfectLabel} ${ok} 路，${faceResult.nurseName}`)
      return
    }
    if (ok > 0) {
      message.warning(`消毒登记：成功 ${ok} 路，失败 ${fail} 路`)
    }
    if (firstErr) message.error(String(firstErr))
  }, [disinfectLabel, disinfectSubmitting, dispatch, effectiveStoreId, fetchDeviceList])

  const handleCellAction = useCallback((actionPayload) => {
    const {
      action,
      type: actionType,
      deviceSerial,
      channelNo,
      nurseId,
      nurseName,
      nurseAvatar,
      cellState
    } = actionPayload || {}
    const streamKey = buildDepartureStreamKey(deviceSerial, channelNo)
    if (!streamKey) return

    const existing = departureByStreamKeyRef.current[streamKey]
    /** 预登记可改走新建单；离室中后端拒绝 POST /departurerecord，改走护理结束 */
    const canReplacePreDeparted = existing?.status === 'pre_departed'
    const canReplaceActive = existing?.status === 'active'
    const nursingActive = canReplaceActive && existing?.type === DEPARTURE_TYPE.NURSING
    const canReregisterDestination = canReplacePreDeparted || canReplaceActive

    const isReturnRoom = action === 'return_room' || actionType === DEPARTURE_TYPE.RETURN_ROOM
    const isNursing = actionType === DEPARTURE_TYPE.NURSING

    if (isReturnRoom || isNursing) {
      if (!nurseId || !nurseName) {
        message.warning('请先刷脸确认身份')
        return
      }
      if (
        existing
        && !canReregisterDestination
        && (existing.status === 'pre_departed' || existing.status === 'active')
      ) {
        message.warning('这路已经登记过了，请先送回或取消再登记')
        return
      }

      const deviceId = String(deviceSerial).trim()
      const channel = String(channelNo).trim()
      if (!deviceId || channel === '') {
        message.warning('缺少设备信息，暂时没法登记')
        return
      }

      const departureType = isNursing ? DEPARTURE_TYPE.NURSING : DEPARTURE_TYPE.RETURN_ROOM
      const subType = isNursing ? action : undefined
      const fromAlarm = cellState === CELL_STATE.ALERT
      const stayActive = fromAlarm || canReplaceActive
      const fallbackId = canReregisterDestination ? existing.id : `dep_${Date.now()}`

      const applyLocalDeparture = (response) => {
        const recordId = response?.data?.id != null && response.data.id !== ''
          ? response.data.id
          : fallbackId
        const devices = mergedDevicesRef.current || []
        let listAlarmMs = 0
        for (let i = 0; i < devices.length; i += 1) {
          const item = devices[i]
          if (buildDepartureStreamKey(item?.deviceSerial, item?.channelNo) !== streamKey) continue
          listAlarmMs = Number(item.alarmTimeMs) || 0
          break
        }
        const row = {
          id: recordId,
          type: departureType,
          ...(isNursing ? { subType } : {}),
          nurseId,
          nurseName,
          status: stayActive ? 'active' : 'pre_departed',
          fromNursing: nursingActive ? existing.subType : undefined
        }
        if (existing?.departedAtFromSse && existing.departedAt) {
          row.departedAt = existing.departedAt
          row.departedAtFromSse = true
        } else if (listAlarmMs > 0) {
          row.departedAt = listAlarmMs
        } else if (existing?.departedAt) {
          row.departedAt = existing.departedAt
        }
        noBabySpeakBlockedKeysRef.current.add(streamKey)
        departureByStreamKeyRef.current = {
          ...departureByStreamKeyRef.current,
          [streamKey]: row
        }
        dispatch(upsertSurveillanceDeparture({ streamKey, row }))
        if (fromAlarm) {
          dispatch(addMonitoringAlarmProcessed({
            deviceId,
            channelNo: channel
          }))
        }
        if (isNursing) {
          const label = (subType != null && nursingLabelMap[String(subType)]?.label) || subType
          message.success(
            canReplaceActive
              ? `下一步：${label}，${nurseName}`
              : (canReregisterDestination
                ? `已改成：${label}，${nurseName}`
                : (fromAlarm
                  ? `已登记：${label}，${nurseName}`
                  : `已登记：${label}，${nurseName}（抱走后自动确认）`))
          )
          return
        }
        message.success(
          canReplaceActive
            ? `下一步：回房，${nurseName} 带走`
            : (canReregisterDestination
              ? `已改成回房，${nurseName} 带走`
              : (fromAlarm
                ? `已登记回房，${nurseName} 带走`
                : `已登记回房，${nurseName} 带走（抱走后自动确认）`))
        )
      }

      const rejectDeparture = (error, fallbackMsg) => {
        noBabySpeakBlockedKeysRef.current.delete(streamKey)
        const errMsg =
          (error && typeof error === 'object' && (error.msg || error.message)) || fallbackMsg
        message.error(String(errMsg))
      }

      noBabySpeakBlockedKeysRef.current.add(streamKey)

      /** 离室中不能再 POST /departurerecord（后端：当前状态不允许登记离室） */
      if (canReplaceActive) {
        return endDepartureNursing({
          deviceId,
          channelNo: channel,
          nurseId,
          nextStep: resolveNursingNextStep(isNursing ? DEPARTURE_TYPE.NURSING : DEPARTURE_TYPE.RETURN_ROOM),
          nextSubType: isNursing ? subType : '',
          nurseAvatar
        })
          .then(applyLocalDeparture)
          .catch((error) => rejectDeparture(error, '登记下一步失败'))
      }

      return createDepartureRecord({
        deviceId,
        channelNo: channel,
        nurseId,
        type: departureType,
        subType,
        nurseAvatar
      })
        .then(applyLocalDeparture)
        .catch((error) => rejectDeparture(error, '登记去向失败'))
    }

    if (action === 'cancel_departure') {
      if (!existing || existing.status !== 'pre_departed') {
        message.info('只有还没抱走时才能取消')
        return
      }
      if (!nurseId) {
        message.warning('请先刷脸确认身份')
        return
      }
      const deviceId = String(deviceSerial).trim()
      const channel = String(channelNo).trim()
      if (!deviceId || channel === '') {
        message.warning('缺少设备信息，暂时没法取消')
        return
      }
      deleteDepartureRecord({
        deviceId,
        channelNo: channel,
        nurseId
      })
        .then(() => {
          noBabySpeakBlockedKeysRef.current.delete(streamKey)
          const next = { ...departureByStreamKeyRef.current }
          delete next[streamKey]
          departureByStreamKeyRef.current = next
          dispatch(removeSurveillanceDeparture({ streamKey }))
          message.success('已取消登记')
        })
        .catch((error) => {
          const errMsg =
            (error && typeof error === 'object' && (error.msg || error.message)) || '取消登记失败'
          message.error(String(errMsg))
        })
      return
    }

    // 手动送回 / 回监护室：须已有离室单（active）；预登记请用取消登记
    if (action === 'return_nursery') {
      if (
        !existing
        || (existing.status !== 'active' && existing.status !== 'return_awaiting')
      ) {
        message.info('请先登记去向，再手动送回')
        return
      }
      if (existing.status === 'return_awaiting') {
        message.info('已在待确认，等画面看到宝宝后再确认')
        return
      }

      const deviceId = String(deviceSerial).trim()
      const channel = String(channelNo).trim()
      if (!deviceId || channel === '') {
        message.warning('缺少设备信息，暂时没法送回')
        return
      }

      /** 护理结束 → 回监护室：走 nursing/end（nextStep=return_nursery） */
      if (nursingActive) {
        if (!nurseId || !nurseAvatar || typeof Blob === 'undefined' || !(nurseAvatar instanceof Blob)) {
          message.warning('请先刷脸确认身份')
          return
        }
        endDepartureNursing({
          deviceId,
          channelNo: channel,
          nurseId,
          nextStep: NURSING_NEXT_STEP.RETURN_NURSERY,
          nurseAvatar
        })
          .then(() => {
            dispatch(applySurveillanceReturnPending({ streamKey }))
            message.success(`已回监护室${nurseName ? `，${nurseName}` : ''}，等待系统确认回在位`)
          })
          .catch((error) => {
            const errMsg =
              (error && typeof error === 'object' && (error.msg || error.message)) || '回监护室失败'
            message.error(String(errMsg))
          })
        return
      }

      const alarm = sceneByStreamKeyRef.current.get(streamKey)
      // 画面未见宝宝：只记待确认；真正关单等推理见婴由 Java 自动确认
      if (getMonitoringSceneFlags(alarm?.seeData).hasBaby !== true) {
        const nextRow = {
          ...existing,
          status: 'return_awaiting',
          priorStatus: existing.status,
          returnAt: Date.now(),
          autoReturn: false
        }
        departureByStreamKeyRef.current = {
          ...departureByStreamKeyRef.current,
          [streamKey]: nextRow
        }
        dispatch(markSurveillanceReturnAwaiting({ streamKey }))
        message.info(`画面上还没看到宝宝，已记为待确认（${RETURN_AWAITING_GRACE_MINUTES}分钟内未见将恢复登记）`)
        return
      }

      // 画面已见宝宝：调 confirm-return（人工确认），须刷脸 nurseId + nurseAvatar
      if (!nurseId || !nurseAvatar || typeof Blob === 'undefined' || !(nurseAvatar instanceof Blob)) {
        message.warning('请先刷脸确认身份')
        return
      }

      confirmDepartureRecordReturn({
        deviceId,
        channelNo: channel,
        nurseId,
        nurseAvatar
      })
        .then(() => {
          dispatch(applySurveillanceReturnPending({ streamKey }))
          message.success('已确认送回，等待系统回在位')
        })
        .catch((error) => {
          const errMsg =
            (error && typeof error === 'object' && (error.msg || error.message)) || '确认送回失败'
          message.error(String(errMsg))
        })
      return
    }

    if (action === 'wrong_check') {
      if (wrongCheckPendingRef.current[streamKey]) return
      const deviceId = String(deviceSerial).trim()
      const channel = String(channelNo).trim()
      if (!deviceId || channel === '') {
        message.warning('缺少设备信息，暂时没法标记')
        return
      }
      const devices = mergedDevicesRef.current || []
      for (let i = 0; i < devices.length; i += 1) {
        const item = devices[i]
        if (buildDepartureStreamKey(item?.deviceSerial, item?.channelNo) !== streamKey) continue
        if (item.isFalsePositive === true) return
        break
      }
      const releaseWrongCheck = () => {
        const next = { ...wrongCheckPendingRef.current }
        delete next[streamKey]
        wrongCheckPendingRef.current = next
        setWrongCheckPending(next)
      }
      wrongCheckPendingRef.current = {
        ...wrongCheckPendingRef.current,
        [streamKey]: true
      }
      setWrongCheckPending(wrongCheckPendingRef.current)
      markYingshiDeviceFalsePositive({ deviceId, channelNo: channel })
        .then(() => {
          dispatch(addMonitoringAlarmProcessed({ deviceId, channelNo: channel }))
          const storeId = effectiveStoreIdRef.current
          message.success('已标记为识别有误')
          if (!storeId) return null
          return fetchDeviceList({ storeId })
        })
        .catch((error) => {
          const errMsg =
            (error && typeof error === 'object' && (error.msg || error.message)) || '标记失败'
          message.error(String(errMsg))
        })
        .finally(releaseWrongCheck)
      return
    }

    message.info(`操作: ${action}`)
  }, [dispatch, nursingLabelMap, fetchDeviceList])

  const showEmpty = !listPending && (!effectiveStoreId || displayDevices.length === 0)
  const emptyDescription = !effectiveStoreId
    ? (canSelectStore ? '请先选择店铺' : '当前账号未关联店铺，暂时看不了监控')
    : inferenceOnly && mergedDevices.length > 0 ? '本店暂无开启婴儿监控的摄像头' : '本店暂无设备'

  useEffect(() => {
    const el = containerRef.current
    if (!el) return undefined
    updateContainerSize()
    let rafId = 0
    const scheduleMeasure = () => {
      if (rafId) return
      rafId = window.requestAnimationFrame(() => {
        rafId = 0
        updateContainerSize()
      })
    }
    const ro = new ResizeObserver(scheduleMeasure)
    ro.observe(el)
    window.addEventListener('resize', scheduleMeasure)
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId)
      ro.disconnect()
      window.removeEventListener('resize', scheduleMeasure)
    }
  }, [updateContainerSize, screenLayoutId, showEmpty, listPending, displayDevices.length])

  return (
    <div
      ref={pageRef}
      className={[
        'surveillance-list-page',
        isTabletDensity ? 'surveillance-list-page--tablet' : '',
        wallFullscreen ? 'surveillance-list-page--wall-fullscreen' : ''
      ].filter(Boolean).join(' ')}
      data-screen-count={screenLayoutId}
      data-density={isTabletDensity ? 'tablet' : 'desktop'}
      onPointerDownCapture={markAlertTtsAudioUnlocked}
    >
      {disinfectSubmitting ? (
        <div className="surveillance-disinfect-wait" role="status">
          <LoadingOutlined spin />
          <span>{disinfectWaitText || '正在登记消毒…'}</span>
        </div>
      ) : null}
      <div className="surveillance-toolbar">
        <SelectC
          label=""
          query={toolbarQuery}
          searchKey="storeId"
          options={storeSelectOptions}
          action={fetchDeviceList}
          propsClassName="surveillance-toolbar__store"
          propsClassSpan="surveillance-toolbar__label-hidden"
          style={{ minWidth: isTabletDensity ? 140 : 160 }}
          modalProps={{
            disabled: !canSelectStore,
            allowClear: false,
            showSearch: canSelectStore,
            placeholder: canSelectStore ? '请选择店铺' : '未关联店铺',
            suffixIcon: <ShopOutlined />
          }}
        />
        <div className="surveillance-toolbar__inference-filter">
          <span className={['surveillance-toolbar__inference-label', !inferenceOnly ? 'surveillance-toolbar__inference-label--active' : ''].filter(Boolean).join(' ')}>
            全部摄像头
          </span>
          <Switch className="surveillance-toolbar__inference-switch" checked={inferenceOnly} onChange={handleInferenceOnlyChange} size="small" />
          <span className={['surveillance-toolbar__inference-label', inferenceOnly ? 'surveillance-toolbar__inference-label--active' : ''].filter(Boolean).join(' ')}>
            婴儿监控
          </span>
        </div>
        {isTabletDensity ? (
          <div className="surveillance-toolbar__layout-chips" role="group" aria-label="快捷分屏">
            {TABLET_LAYOUT_CHIPS.map((chipId) => (
              <button
                key={chipId}
                type="button"
                className={[
                  'surveillance-toolbar__layout-chip',
                  String(screenLayoutId) === chipId ? 'surveillance-toolbar__layout-chip--active' : ''
                ].filter(Boolean).join(' ')}
                onClick={handleLayoutChipClick(chipId)}
              >
                {chipId}
              </button>
            ))}
          </div>
        ) : null}
        <SelectC
          label=""
          query={toolbarQuery}
          searchKey="screenLayoutId"
          options={SCREEN_LAYOUT_OPTIONS}
          actionChange={handleScreenLayoutChange}
          propsClassName="surveillance-toolbar__layout"
          propsClassSpan="surveillance-toolbar__label-hidden"
          style={{ width: 100 }}
          modalProps={{ allowClear: false, showSearch: false }}
        />
        <div className="surveillance-toolbar__actions">
          <Permission rootPath="surveillancePage">
            <span pathitem={['faceLibrary']} checkarray={['buttonCodes']}>
              <Button
                type="default"
                size="small"
                className="surveillance-toolbar__tts-btn"
                icon={<UserOutlined />}
                onClick={() => setFaceLibraryOpen(true)}
              >
                本店人脸库
              </Button>
            </span>
          </Permission>
          <Button
            type="default"
            size="small"
            className="surveillance-toolbar__tts-btn"
            icon={<SafetyCertificateOutlined />}
            disabled={disinfectOpen || disinfectSubmitting}
            onClick={openDisinfect}
          >
            一键消毒
          </Button>
          <SurveillanceAlertTtsSettingsModal>
            <Button
              type="default"
              size="small"
              className="surveillance-toolbar__tts-btn"
              icon={<SoundOutlined />}
            >
              语音设置
            </Button>
          </SurveillanceAlertTtsSettingsModal>
          <Button
            type="default"
            size="small"
            className="surveillance-toolbar__tts-btn"
            icon={<FullscreenOutlined />}
            onClick={enterWallFullscreen}
          >
            全屏
          </Button>
        </div>
      </div>

      {wallFullscreen ? (
        <Button
          type="default"
          size="small"
          className={[
            'surveillance-wall-fullscreen-exit',
            exitChromeVisible ? '' : 'surveillance-wall-fullscreen-exit--idle-hidden'
          ].filter(Boolean).join(' ')}
          icon={<FullscreenExitOutlined />}
          onClick={leaveWallFullscreen}
          aria-label="退出全屏"
          tabIndex={exitChromeVisible ? 0 : -1}
        >
          退出全屏
        </Button>
      ) : null}

      <StoreFaceLibraryDrawer
        open={faceLibraryOpen}
        onClose={() => setFaceLibraryOpen(false)}
        storeId={effectiveStoreId}
      />

      <Modal
        open={disinfectOpen}
        onCancel={closeDisinfect}
        footer={null}
        forceRender
        width={isTabletDensity ? '100%' : 420}
        closable={false}
        maskClosable={!disinfectSubmitting}
        centered={!isTabletDensity}
        wrapClassName="surveillance-glass-panel-wrap"
        styles={{
          mask: { background: 'rgba(0, 0, 0, 0.72)' },
          content: {
            background: 'transparent',
            boxShadow: 'none',
            padding: 0
          },
          body: { padding: 0, background: 'transparent' }
        }}
      >
        <div className="surveillance-glass-panel">
          <div className="surveillance-glass-panel__header">
            <div className="surveillance-glass-panel__title-wrap">
              <SafetyCertificateOutlined className="surveillance-glass-panel__title-icon" />
              <div className="surveillance-glass-panel__title-block">
                <h3 className="surveillance-glass-panel__title">一键消毒</h3>
                <p className="surveillance-glass-panel__title-hint">
                  刷脸后，为未登记去向的摄像头登记{disinfectLabel}
                </p>
              </div>
            </div>
          </div>
          <FaceCapture
            ref={disinfectFaceRef}
            sessionKey={DISINFECT_FACE_KEY}
            active={disinfectOpen}
            onChange={setDisinfectFace}
          />
          <div className="surveillance-glass-panel__footer">
            <span className="surveillance-glass-panel__footer-text">
              {disinfectTargets.length > 0
                ? `未登记去向 ${disinfectTargets.length} 路`
                : '这些摄像头都已有去向'}
            </span>
            <div className="surveillance-glass-panel__footer-actions">
              <Button
                type="text"
                className="surveillance-glass-panel__footer-cancel"
                disabled={disinfectSubmitting}
                onClick={closeDisinfect}
              >
                取消
              </Button>
              <Button
                type="primary"
                disabled={
                  disinfectSubmitting
                  || disinfectTargets.length === 0
                  || !isFaceReadyForDestination(disinfectFace)
                }
                loading={disinfectSubmitting}
                onClick={confirmDisinfect}
              >
                登记消毒
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {listPending ? <div className="surveillance-loading">加载中...</div> : null}

      <div ref={containerRef} className="surveillance-container">
        {showEmpty ? (
          <Empty className="surveillance-empty" description={emptyDescription} />
        ) : (
          <div
            className="surveillance-grid"
            style={{
              gridTemplateColumns: gridMetrics.cellWidth > 0 && cols > 0
                ? `repeat(${cols}, minmax(0, ${gridMetrics.cellWidth}px))`
                : (cols > 0 ? `repeat(${cols}, minmax(0, 1fr))` : undefined)
            }}
          >
            {devicesToShow.map((device, index) => {
              const streamKey = buildMonitoringStreamKeyFromDevice(device.deviceSerial, device.channelNo)
              const inferenceOn = device.needInference === true
              const sceneEntry = sceneByStreamKey.get(streamKey) || null
              const departureInfo = departureByStreamKey[streamKey] || null
              const alarmCellState = !inferenceOn
                ? CELL_STATE.EMPTY
                : resolveCellState(device, sceneEntry)

              // 顶栏与 ALERT 同生共死：有 ALERT 必拼载荷；拼不出不当警；离室让位清顶栏
              let monitoringAlarm = null
              if (alarmCellState === CELL_STATE.ALERT) {
                const raw = sceneEntry || buildListInitSceneEntry(device)
                if (raw) {
                  const withLevel = enrichSurveillanceAlarmLevel(raw, device)
                  const eventTimeMs = resolveSurveillanceAlarmEventTimeMs(withLevel, device)
                  monitoringAlarm = eventTimeMs === withLevel.eventTimeMs
                    ? withLevel
                    : { ...withLevel, eventTimeMs }
                }
              }
              let cellState = resolveEffectiveCellState(
                (alarmCellState === CELL_STATE.ALERT && !monitoringAlarm)
                  ? CELL_STATE.IN_PLACE
                  : alarmCellState,
                departureInfo
              )
              if (monitoringAlarm && isLocalDepartureCellState(cellState)) {
                monitoringAlarm = null
              }
              const isFalsePositive = device.isFalsePositive === true
              // 列表已标错检：收掉这条告警，显示已点错检
              if (isFalsePositive && cellState === CELL_STATE.ALERT) {
                monitoringAlarm = null
                cellState = CELL_STATE.IN_PLACE
              }

              const fromListInit = Boolean(sceneEntry?.fromListInit)
              const showDeviceAlarming = Boolean(monitoringAlarm)
                && fromListInit
                && device.isAlarming === true
              const sceneThumb = (sceneEntry?.thumb || '').trim()
              const sceneFull = (sceneEntry?.fullImage || '').trim()
              // SSE 到了：图只认场景；list 截图仅首屏空档回补
              const deviceThumb = fromListInit || !sceneEntry
                ? (device.latestMonitoringImageThumbnail || '').trim()
                : ''
              const deviceFull = fromListInit || !sceneEntry
                ? (device.latestMonitoringImage || '').trim()
                : ''
              const needsTick = cellState === CELL_STATE.ALERT
                || cellState === CELL_STATE.DEPARTED
                || cellState === CELL_STATE.NURSING_IN_PROGRESS
                || Boolean(monitoringAlarm?.eventTimeMs)

              return (
                <SurveillanceCell
                  key={`${device.deviceSerial}_${device.channelNo || 1}`}
                  device={device}
                  index={index}
                  slotIndex={index}
                  cellState={cellState}
                  departureInfo={departureInfo}
                  monitoringAlarm={monitoringAlarm}
                  isFalsePositive={isFalsePositive}
                  wrongCheckPending={wrongCheckPending[streamKey] === true}
                  monitoringBaselineReady={monitoringBaselineReady}
                  latestMonitoringImage={sceneFull || deviceFull}
                  latestMonitoringImageThumbnail={sceneThumb || deviceThumb}
                  deviceIsAlarming={showDeviceAlarming}
                  alarmDisplayEligible={inferenceOn}
                  gridMetrics={gridMetrics}
                  onCellAction={handleCellAction}
                  isTabletDensity={isTabletDensity}
                  nowTick={needsTick ? nowTick : 0}
                />
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
